import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useLocationStore } from '@/store/location';
import { useDeviceId } from './useDeviceId';

/** FOH stock warning behavior options */
export type FohStockWarningBehavior = 'warn_allow' | 'block_hard' | 'require_pin';

export interface LocationSettings {
  hide_default_payment_types?: boolean;
  appearance?: {
    accentColor?: string;
    theme?: 'light' | 'dark' | 'system';
    font?: string;
  };
  icon?: string | null;
  icon_url?: string | null;
  /** Whether new products should have inventory tracking enabled by default (defaults to true when undefined) */
  default_track_inventory?: boolean;
  /** How to handle sales when FOH stock is insufficient */
  foh_stock_warning_behavior?: FohStockWarningBehavior;
}

export interface Location {
  id: string;
  name: string;
  code?: string | null;
  organization_id: string;
  address?: { line1?: string; city?: string; parish?: string } | null;
  phone?: string | null;
  email?: string | null;
  is_active: boolean;
  settings?: LocationSettings | null;
}

/**
 * Generate a location code from the location name.
 * Takes the first word (or abbreviation) and adds a sequence number.
 * E.g., "KFTL" -> "KFTL-0001", "Main Location" -> "MAIN-0001"
 */
export function generateLocationCode(name: string, existingCodes: string[]): string {
  // Extract the base code from the name (first word, uppercase, max 10 chars)
  const words = name.trim().split(/\s+/);
  let baseCode = words[0].toUpperCase().replace(/[^A-Z0-9]/g, '');
  
  // If first word is very short (1-2 chars), try to use initials of all words
  if (baseCode.length <= 2 && words.length > 1) {
    baseCode = words.map(w => w.charAt(0).toUpperCase()).join('');
  }
  
  // Limit to 10 characters
  baseCode = baseCode.substring(0, 10);
  
  if (!baseCode) {
    baseCode = 'LOC';
  }

  // Find the highest existing sequence number for this base code
  let maxSequence = 0;
  const codePattern = new RegExp(`^${baseCode}-(\\d+)$`, 'i');
  
  for (const existingCode of existingCodes) {
    const match = existingCode?.match(codePattern);
    if (match) {
      const seq = parseInt(match[1], 10);
      if (seq > maxSequence) {
        maxSequence = seq;
      }
    }
  }

  // Generate the next sequence number (4 digits, zero-padded)
  const nextSequence = String(maxSequence + 1).padStart(4, '0');
  
  return `${baseCode}-${nextSequence}`;
}

export function useLocations() {
  const { user, profile } = useAuth();
  const { currentLocation, setCurrentLocation, setLocations } = useLocationStore();
  const { deviceId } = useDeviceId();
  const queryClient = useQueryClient();
  const queryKey = ['locations', user?.id];

  // Check if user is a cashier/employee (needs device-based location restriction)
  const isCashierRole = profile?.role === 'cashier' || profile?.role === 'employee';

  // Define the type for device registration with location
  type DeviceRegistrationWithLocation = {
    id: string;
    location_id: string;
    is_active: boolean;
    location?: Location | Location[];
  };

  // Fetch device registration to get device's assigned location
  const { data: deviceRegistration, isLoading: deviceLoading } = useQuery({
    queryKey: ['device-location', deviceId, profile?.organization_id],
    queryFn: async (): Promise<DeviceRegistrationWithLocation | null> => {
      if (!deviceId || !profile?.organization_id) return null;

      try {
        // Try with location join first
        const { data, error } = await supabase
          .from('registered_devices')
          .select('id, location_id, is_active, location:locations(id, name, code, organization_id, address, phone, email, is_active, settings)')
          .eq('device_id', deviceId)
          .eq('organization_id', profile.organization_id)
          .eq('is_active', true)
          .maybeSingle();

        if (!error && data) {
          return data as unknown as DeviceRegistrationWithLocation;
        }
        
        // If join fails (406 or other), try simple query
        if (error && error.code !== 'PGRST116') {
          console.warn('[useLocations] Device registration join query failed, trying simple query:', error.code);
          const { data: simpleData, error: simpleError } = await supabase
            .from('registered_devices')
            .select('id, location_id, is_active')
            .eq('device_id', deviceId)
            .eq('organization_id', profile.organization_id)
            .eq('is_active', true)
            .maybeSingle();
          
          if (!simpleError && simpleData) {
            return simpleData as DeviceRegistrationWithLocation;
          }
          console.error('[useLocations] Error fetching device registration:', simpleError);
        }
        
        return null;
      } catch (err) {
        console.error('[useLocations] Unexpected error fetching device registration:', err);
        return null;
      }
    },
    enabled: !!deviceId && !!profile?.organization_id && isCashierRole,
  });

  const { data: allLocations, isLoading: locationsLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user || !profile) return [];

      // Superadmins can see ALL locations across all organizations
      if (profile.role === 'superadmin') {
        const { data: allLocs, error: allError } = await supabase
          .from('locations')
          .select('id, name, code, organization_id, address, phone, email, is_active, settings')
          .order('name');

        if (allError) {
          console.error('Error fetching all locations:', allError);
          return [];
        }

        return allLocs as Location[];
      }

      // Admin-level users can see ALL locations in their organization
      const adminRoles = ['primary_admin', 'owner', 'admin'];
      if (profile.organization_id && adminRoles.includes(profile.role)) {
        const { data: orgLocations, error: orgError } = await supabase
          .from('locations')
          .select('id, name, code, organization_id, address, phone, email, is_active, settings')
          .eq('organization_id', profile.organization_id)
          .order('name');

        if (orgError) {
          console.error('Error fetching org locations:', orgError);
          return [];
        }

        return orgLocations as Location[];
      }

      // Non-admin users: Get locations via user_location_access
      const { data: accessData, error: accessError } = await supabase
        .from('user_location_access')
        .select('location_id')
        .eq('user_id', user.id);

      if (accessError) {
        console.error('Error fetching location access:', accessError);
        return [];
      }

      if (!accessData || accessData.length === 0) {
        return [];
      }

      const locationIds = (accessData as any[]).map((a) => a.location_id);

      const { data: locationData, error: locationError } = await supabase
        .from('locations')
        .select('id, name, code, organization_id, address, phone, email, is_active, settings')
        .in('id', locationIds)
        .order('name');

      if (locationError) {
        console.error('Error fetching locations:', locationError);
        return [];
      }

      return locationData as Location[];
    },
    enabled: !!user && !!profile,
  });

  // For cashiers with registered devices, restrict to device's location only
  const locations = useMemo(() => {
    if (!allLocations) return [];

    // If user is a cashier and device is registered, only show device's location
    if (isCashierRole && deviceRegistration?.location) {
      // Supabase returns nested joins as arrays, get first element
      const locationData = Array.isArray(deviceRegistration.location)
        ? deviceRegistration.location[0]
        : deviceRegistration.location;

      if (locationData) {
        const deviceLocation = locationData as Location;
        // Ensure the device's location is in the user's access list
        const hasAccess = allLocations.some(loc => loc.id === deviceLocation.id);
        if (hasAccess) {
          return [deviceLocation];
        }
      }
      // Device's location not in access list - return empty (will show error state)
      return [];
    }

    return allLocations;
  }, [allLocations, isCashierRole, deviceRegistration]);

  const isLoading = locationsLoading || (isCashierRole && deviceLoading);

  // Create location mutation with optimistic update
  const createLocation = useMutation({
    mutationFn: async (locationData: Partial<Location>) => {
      if (!profile?.organization_id) {
        throw new Error('You must be assigned to an organization to create locations. Please contact your administrator.');
      }
      
      // Check if user has admin role
      const adminRoles = ['superadmin', 'primary_admin', 'owner', 'admin'];
      if (!adminRoles.includes(profile.role)) {
        throw new Error('You do not have permission to create locations. Admin or higher role required.');
      }

      // Check license limit before creating
      try {
        const { data: canAddData } = await supabase.rpc('can_add_resource', { p_resource_type: 'location' });
        const canAdd = canAddData?.[0];
        if (canAdd && !canAdd.can_add) {
          throw new Error(canAdd.message || 'Location limit reached. Please upgrade your plan to add more locations.');
        }
      } catch (licenseError: any) {
        // If the function doesn't exist yet, continue (backward compatibility)
        if (licenseError?.code !== '42883' && licenseError?.message?.includes('limit')) {
          throw licenseError;
        }
      }
      
      // Generate location code from name if not provided
      let locationCode = locationData.code;
      if (!locationCode && locationData.name) {
        // Get existing codes for this organization
        const { data: existingLocations } = await supabase
          .from('locations')
          .select('code')
          .eq('organization_id', profile.organization_id);
        
        const existingCodes = (existingLocations || [])
          .map(loc => loc.code)
          .filter((code): code is string => !!code);
        
        locationCode = generateLocationCode(locationData.name, existingCodes);
      }
      
      const { data, error } = await supabase
        .from('locations')
        .insert({
          organization_id: profile.organization_id,
          name: locationData.name!,
          code: locationCode,
          address: locationData.address || null,
          phone: locationData.phone || null,
          email: locationData.email || null,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        // Parse common Supabase RLS errors
        if (error.code === '42501' || error.message?.includes('policy')) {
          throw new Error('Permission denied. You may not have the required role to create locations.');
        }
        if (error.code === '23505') {
          throw new Error('A location with this name already exists.');
        }
        throw new Error(error.message || 'Failed to create location. Please try again.');
      }
      return data as Location;
    },
    onMutate: async (newLocationData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousLocations = queryClient.getQueryData<Location[]>(queryKey);

      // Optimistically add the new location
      if (previousLocations && profile?.organization_id) {
        // Generate optimistic code
        const existingCodes = previousLocations
          .map(loc => loc.code)
          .filter((code): code is string => !!code);
        const optimisticCode = newLocationData.code || 
          (newLocationData.name ? generateLocationCode(newLocationData.name, existingCodes) : null);

        const optimisticLocation: Location = {
          id: `temp-${Date.now()}`, // Temporary ID
          organization_id: profile.organization_id,
          name: newLocationData.name!,
          code: optimisticCode,
          address: newLocationData.address || null,
          phone: newLocationData.phone || null,
          email: newLocationData.email || null,
          is_active: true,
        };
        queryClient.setQueryData<Location[]>(queryKey, [...previousLocations, optimisticLocation]);
      }

      return { previousLocations };
    },
    onError: (_err, _newLocation, context) => {
      // Rollback on error
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
    },
    onSettled: () => {
      // Refetch to get the real data with proper IDs
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update location mutation with optimistic update
  const updateLocation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Location> }) => {
      const { data, error } = await supabase
        .from('locations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (error.code === '42501' || error.message?.includes('policy')) {
          throw new Error('Permission denied. You may not have the required role to update locations.');
        }
        throw new Error(error.message || 'Failed to update location. Please try again.');
      }
      return data as Location;
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousLocations = queryClient.getQueryData<Location[]>(queryKey);

      // Optimistically update the location
      if (previousLocations) {
        queryClient.setQueryData<Location[]>(
          queryKey,
          previousLocations.map((loc) =>
            loc.id === id ? { ...loc, ...updates } : loc
          )
        );
      }

      return { previousLocations };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Delete location mutation with optimistic update
  const deleteLocation = useMutation({
    mutationFn: async (id: string) => {
      // First verify the location exists and user can see it
      const { data: existingLocation } = await supabase
        .from('locations')
        .select('id')
        .eq('id', id)
        .single();

      if (!existingLocation) {
        throw new Error('Location not found.');
      }

      // Try regular delete first
      const { error, count } = await supabase
        .from('locations')
        .delete({ count: 'exact' })
        .eq('id', id);

      if (error) {
        // If FK conflict (409/23503), try force delete for superadmins
        if (error.code === '23503' || error.message?.includes('conflict')) {
          const { error: forceError } = await supabase.rpc('force_delete_location', {
            p_location_id: id,
          });

          if (forceError) {
            if (forceError.message?.includes('superadmin')) {
              throw new Error('Cannot delete this location. It has associated data and only superadmins can force delete.');
            }
            throw new Error(forceError.message || 'Failed to delete location.');
          }

          return id;
        }

        if (error.code === '42501' || error.message?.includes('policy')) {
          throw new Error('Permission denied. Only superadmins and business owners can delete locations.');
        }
        throw new Error(error.message || 'Failed to delete location. Please try again.');
      }

      // Check if any rows were actually deleted (RLS might block without error)
      if (count === 0) {
        throw new Error('Permission denied. Only superadmins and business owners can delete locations.');
      }

      return id;
    },
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey });

      const previousLocations = queryClient.getQueryData<Location[]>(queryKey);

      // Optimistically remove the location
      if (previousLocations) {
        queryClient.setQueryData<Location[]>(
          queryKey,
          previousLocations.filter((loc) => loc.id !== deletedId)
        );
      }

      return { previousLocations };
    },
    onError: (_err, _deletedId, context) => {
      if (context?.previousLocations) {
        queryClient.setQueryData(queryKey, context.previousLocations);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update location settings mutation
  const updateLocationSettings = useMutation({
    mutationFn: async ({ id, settings }: { id: string; settings: Partial<LocationSettings> }) => {
      // Get current settings first to merge
      const { data: current } = await supabase
        .from('locations')
        .select('settings')
        .eq('id', id)
        .single();

      const mergedSettings = {
        ...(current?.settings || {}),
        ...settings,
      };

      const { data, error } = await supabase
        .from('locations')
        .update({ settings: mergedSettings })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Location;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update store when locations change
  useEffect(() => {
    if (locations) {
      setLocations(locations);

      // Auto-select first location if none selected
      if (!currentLocation && locations.length > 0) {
        setCurrentLocation(locations[0]);
      }

      // If current location exists, sync it with the latest data from the database
      // This ensures name changes and other updates are reflected
      if (currentLocation && locations.length > 0) {
        const freshLocation = locations.find((l) => l.id === currentLocation.id);
        if (freshLocation) {
          // Update with fresh data if anything changed (name, settings, etc.)
          if (
            freshLocation.name !== currentLocation.name ||
            freshLocation.phone !== currentLocation.phone ||
            freshLocation.email !== currentLocation.email ||
            JSON.stringify(freshLocation.settings) !== JSON.stringify(currentLocation.settings)
          ) {
            setCurrentLocation(freshLocation);
          }
        } else {
          // Location no longer in the list, reset to first
          setCurrentLocation(locations[0]);
        }
      }
    }
  }, [locations, currentLocation, setLocations, setCurrentLocation]);

  return {
    locations: locations ?? [],
    currentLocation,
    isLoading,
    setCurrentLocation,
    refetch,
    createLocation,
    updateLocation,
    updateLocationSettings,
    deleteLocation,
    // Device-based location info for cashiers
    isDeviceLocked: isCashierRole && !!deviceRegistration?.location,
    deviceLocation: deviceRegistration?.location
      ? (Array.isArray(deviceRegistration.location)
          ? deviceRegistration.location[0]
          : deviceRegistration.location) as Location | null
      : null,
    deviceRegistration,
  };
}

